import type { Question, RichText, SurveyConfig } from './types';
import { displayIndexMap, pageNumberMap } from './question-utils';

export type SearchScope = 'all' | 'titles' | 'descriptions' | 'options' | 'labels';
export type SearchOptions = { query: string; caseSensitive: boolean; wholeField: boolean; scope: SearchScope };
type Path = (string | number)[];
export type SearchField = {
  key: string;
  path: Path;
  text: string;
  label: string;
  location: string;
  domKey: string;
  questionId?: string;
  maxLength?: number;
};
export type SearchMatch = { key: string; field: SearchField; start: number; end: number };
export type ReplacementResult = { config: SurveyConfig; count: number };

type TextPart = { node: Text; start: number; end: number };
/** Read text nodes only: HTML attributes, links and image URLs are never searched or replaced. */
function richDocument(html: string) {
  const template = document.createElement('template');
  template.innerHTML = html;
  const parts: TextPart[] = [];
  let text = '';
  let trailingBlockBreak = false;
  const block = /^(DIV|P|H[1-6]|LI|UL|OL|BLOCKQUOTE|PRE)$/;
  const walk = (node: Node) => {
    if (node.nodeType === 3) {
      const value = (node.nodeValue ?? '').replace(/\u00a0/g, ' ');
      parts.push({ node: node as Text, start: text.length, end: text.length + value.length });
      text += value;
      if (value) trailingBlockBreak = false;
    } else {
      const tag = (node as Element).tagName ?? '';
      if (/^(SCRIPT|STYLE|NOSCRIPT|IFRAME|OBJECT)$/.test(tag)) return;
      if (tag === 'BR') { text += '\n'; trailingBlockBreak = false; return; }
      const isBlock = block.test(tag);
      if (isBlock && text && !text.endsWith('\n')) text += '\n';
      Array.from(node.childNodes).forEach(walk);
      if (isBlock && text && !text.endsWith('\n')) { text += '\n'; trailingBlockBreak = true; }
    }
  };
  walk(template.content);
  if (trailingBlockBreak) text = text.slice(0, -1);
  return { template, text, parts };
}

function fieldText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'text' in value) {
    const rich = value as RichText;
    return rich.html?.trim() ? richDocument(rich.html).text : rich.text;
  }
  return '';
}

export function surveySearchFields(config: SurveyConfig, scope: SearchScope): SearchField[] {
  const fields: SearchField[] = [];
  const add = (path: Path, value: unknown, group: SearchScope, label: string, location: string, domKey: string, questionId?: string, maxLength?: number) => {
    if (scope !== 'all' && scope !== group) return;
    fields.push({ key: JSON.stringify(path), path, text: fieldText(value), label, location, domKey, questionId, maxLength });
  };
  add(['header', 'mainTitle'], config.header?.mainTitle, 'titles', '问卷标题', '问卷', 'header.mainTitle');
  add(['header', 'subTitle'], config.header?.subTitle, 'descriptions', '问卷描述', '问卷', 'header.subTitle');
  const numbers = displayIndexMap(config.questions);
  const pages = pageNumberMap(config.questions);
  let page = 1;
  config.questions.forEach((q, i) => {
    if (q.type === 'pagebreak') { page = (pages.get(q.id) ?? page) + 1; return; }
    const location = q.type === 'statement' ? `第 ${page} 页 · 标题说明` : `第 ${page} 页 · 第 ${numbers.get(q.id)} 题`;
    const field = (path: Path, value: unknown, group: SearchScope, label: string, domKey: string, maxLength?: number) =>
      add(['questions', i, ...path], value, group, label, location, domKey, q.id, maxLength);
    field(['title'], q.title, 'titles', q.type === 'statement' ? '标题说明' : '题干', 'title');
    field(['description'], q.description, 'descriptions', '描述', 'description');
    if (['single', 'multi', 'select', 'ranking'].includes(q.type)) {
      q.options?.forEach((option, j) => field(typeof option === 'string' ? ['options', j] : ['options', j, 'value'], typeof option === 'string' ? option : option.value, 'options', `选项 ${j + 1}`, `option-${j}`));
      for (const name of ['minSelectHint', 'maxSelectHint'] as const) {
        field(['params', name], q.params?.[name], 'labels', name === 'minSelectHint' ? '最少选择提示' : '最多选择提示', name, 40);
      }
    }
    if (['slider', 'matrixslider'].includes(q.type)) {
      for (const [name, label, limit] of [['minLabel', '最小值显示文本', 24], ['maxLabel', '最大值显示文本', 24], ['unit', '单位', 6]] as const) {
        field(['params', name], q.params?.[name], 'labels', label, name, limit);
      }
    }
    if (['matrix', 'matrixtext', 'matrixslider'].includes(q.type)) {
      q.params?.rows?.forEach((row, j) => {
        field(['params', 'rows', j], row, 'options', `行 ${j + 1}`, `row-${j}`);
        if (q.type === 'matrixtext') q.params?.rowControls?.[row]?.options?.forEach((option, k) =>
          field(['params', 'rowControls', row, 'options', k], option, 'options', `行 ${j + 1} · 选项 ${k + 1}`, `row-${j}-option-${k}`));
      });
      if (q.type === 'matrix') q.params?.columns?.forEach((column, j) => field(['params', 'columns', j], column, 'options', `列 ${j + 1}`, `column-${j}`));
      if (q.type === 'matrixtext') q.params?.textColumns?.forEach((column, j) => {
        field(['params', 'textColumns', j, 'title'], column.title, 'options', `列 ${j + 1}`, `text-column-${j}`);
        column.options?.forEach((option, k) => field(['params', 'textColumns', j, 'options', k], option, 'options', `列 ${j + 1} · 选项 ${k + 1}`, `text-column-${j}-option-${k}`));
      });
    }
  });
  return fields;
}

export function findSurveyMatches(config: SurveyConfig, options: SearchOptions): SearchMatch[] {
  if (!options.query) return [];
  // Literal text, including $, brackets and backslashes; never interpret user input as a regex.
  const regex = new RegExp(options.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), options.caseSensitive ? 'g' : 'gi');
  const matches: SearchMatch[] = [];
  for (const field of surveySearchFields(config, options.scope)) {
    regex.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(field.text))) {
      if (options.wholeField && (match.index !== 0 || match[0].length !== field.text.length)) continue;
      matches.push({ key: `${field.key}:${match.index}`, field, start: match.index, end: match.index + match[0].length });
    }
  }
  return matches;
}

function atPath(value: any, path: Path): any { return path.reduce((current, key) => current?.[key], value); }
function withPath(value: any, [head, ...tail]: Path, replacement: unknown): any {
  if (head === undefined) return replacement;
  const next = tail.length ? withPath(value?.[head], tail, replacement) : replacement;
  if (Array.isArray(value)) { const copy = [...value]; copy[Number(head)] = next; return copy; }
  return { ...value, [head]: next };
}

function replaceField(value: string | RichText, matches: SearchMatch[], replacement: string): string | RichText {
  if (typeof value !== 'string' && value.html?.trim()) {
    const { template, parts } = richDocument(value.html);
    // Descending offsets keep all yet-to-be-replaced ranges stable, even across inline tags.
    for (const match of matches) {
      const touched = parts.filter(part => part.end > match.start && part.start < match.end);
      touched.forEach((part, index) => {
        const start = Math.max(0, match.start - part.start);
        const end = Math.min(part.end, match.end) - part.start;
        part.node.data = part.node.data.slice(0, start) + (index === 0 ? replacement : '') + part.node.data.slice(end);
      });
    }
    const html = template.innerHTML;
    return { ...value, html, text: richDocument(html).text };
  }
  let text = fieldText(value);
  for (const match of matches) text = text.slice(0, match.start) + replacement + text.slice(match.end);
  return typeof value === 'string' ? text : { ...value, text };
}

function names(options: Question['options']): string[] { return (options ?? []).map(o => typeof o === 'string' ? o : o.value); }
function renameMap(before: string[] = [], after: string[] = []): Map<string, string> {
  return new Map(before.flatMap((value, i) => after[i] !== undefined && value !== after[i] ? [[value, after[i]] as [string, string]] : []));
}
function renamed(value: string, map?: Map<string, string>): string { return map?.get(value) ?? value; }
function renameKeys<T>(object: Record<string, T>, map: Map<string, string>): Record<string, T> {
  return Object.fromEntries(Object.entries(object).map(([key, value]) => [renamed(key, map), value]));
}

/** Options and matrix labels also act as identifiers. Apply all renames simultaneously. */
function syncReferences(before: SurveyConfig, after: SurveyConfig): SurveyConfig {
  const renames = new Map(before.questions.map((old, i) => {
    const next = after.questions[i];
    return [old.id, {
      options: renameMap(names(old.options), names(next.options)),
      rows: renameMap(old.params?.rows, next.params?.rows),
      columns: renameMap(old.params?.columns, next.params?.columns),
      rowOptions: new Map((old.params?.rows ?? []).map(row => [row, renameMap(old.params?.rowControls?.[row]?.options, next.params?.rowControls?.[row]?.options)])),
    }];
  }));
  const hasRenames = Array.from(renames.values()).some(r => r.options.size || r.rows.size || r.columns.size || Array.from(r.rowOptions.values()).some(m => m.size));
  if (!hasRenames) return after;
  return { ...after, questions: after.questions.map(q => {
    const local = renames.get(q.id)!;
    const next = { ...q };
    if (q.displayLogic?.conditions) next.displayLogic = { ...q.displayLogic, conditions: q.displayLogic.conditions.map(condition => ({
      ...condition,
      value: typeof condition.value === 'string' && ['equals', 'contains'].includes(condition.op) ? renamed(condition.value, renames.get(condition.question)?.options) : condition.value,
    })) };
    if (q.correlation) next.correlation = q.correlation.map(rule => {
      const source = renames.get(rule.questionId);
      return { ...rule,
        ...(rule.row !== undefined ? { row: renamed(rule.row, source?.rows) } : {}),
        ...(rule.optionValue !== undefined ? { optionValue: renamed(rule.optionValue, rule.row !== undefined ? source?.rowOptions.get(rule.row)?.size ? source.rowOptions.get(rule.row) : source?.columns : source?.options) } : {}),
      };
    });
    if (q.optionLinks) next.optionLinks = q.optionLinks.map(link => ({ ...link,
      optionValue: renamed(link.optionValue, renames.get(link.questionId)?.options),
      options: link.options.map(value => renamed(value, local.options)),
    }));
    if (q.jumpLogic?.byOption && local.options.size) next.jumpLogic = { ...q.jumpLogic, byOption: renameKeys(q.jumpLogic.byOption, local.options) };
    if (local.rows.size && q.params) {
      next.params = { ...q.params };
      if (q.params.rowControls) next.params.rowControls = renameKeys(q.params.rowControls, local.rows);
      if (q.params.rowValidations) next.params.rowValidations = renameKeys(q.params.rowValidations, local.rows);
    }
    return next;
  }) };
}

function validateChangedLabels(before: SurveyConfig, after: SurveyConfig) {
  const check = (old: string[] = [], next: string[] = [], label: string) => {
    if (old.length === next.length && old.every((value, i) => value === next[i])) return;
    const trimmed = next.map(value => value.trim());
    if (trimmed.some(value => !value)) throw new Error(`${label}替换后不能为空，本次未作修改。`);
    if (new Set(trimmed).size !== trimmed.length) throw new Error(`${label}替换后出现重名，请调整替换内容。本次未作修改。`);
  };
  before.questions.forEach((old, i) => {
    const next = after.questions[i];
    check(names(old.options), names(next.options), `题目 ${old.id} 的选项`);
    check(old.params?.rows, next.params?.rows, `题目 ${old.id} 的行标题`);
    check(old.params?.columns, next.params?.columns, `题目 ${old.id} 的列标题`);
    check(old.params?.textColumns?.map(c => c.title), next.params?.textColumns?.map(c => c.title), `题目 ${old.id} 的列标题`);
    old.params?.textColumns?.forEach((column, j) => check(column.options, next.params?.textColumns?.[j]?.options, `题目 ${old.id} 的列选项`));
    old.params?.rows?.forEach(row => check(old.params?.rowControls?.[row]?.options, next.params?.rowControls?.[row]?.options, `题目 ${old.id} 的行选项`));
  });
}

export function replaceSurveyMatches(config: SurveyConfig, matches: SearchMatch[], replacement: string): ReplacementResult {
  const groups = new Map<string, SearchMatch[]>();
  for (const match of matches) {
    const list = groups.get(match.field.key) ?? [];
    if (!list.some(item => item.key === match.key)) list.push(match);
    groups.set(match.field.key, list);
  }
  let next = config;
  let count = 0;
  for (const list of groups.values()) {
    const field = list[0].field;
    const value = atPath(config, field.path);
    if (fieldText(value) !== field.text || (field.questionId && config.questions[Number(field.path[1])]?.id !== field.questionId)) {
      throw new Error('内容已变化，请重新查找后再替换。');
    }
    const changed = list.filter(match => field.text.slice(match.start, match.end) !== replacement).sort((a, b) => b.start - a.start);
    if (!changed.length) continue;
    const result = replaceField(value, changed, replacement);
    if (field.maxLength && fieldText(result).length > field.maxLength) throw new Error(`${field.label}最多 ${field.maxLength} 个字符，本次未作修改。`);
    next = withPath(next, field.path, result);
    count += changed.length;
  }
  if (!count) return { config, count: 0 };
  validateChangedLabels(config, next);
  return { config: syncReferences(config, next), count };
}
